import { t } from "@/lib/i18n/interpolate";
import type {
  DocBlock,
  DocSection,
  DocumentModel,
} from "@/lib/tool-kit/document";
import { fillFrame, tidy } from "@/lib/tool-kit/essay/sentence";
import { splitItems } from "@/lib/tool-kit/essay-templates/text";

/**
 * Socratic seminar questions and a printable preparation sheet.
 *
 * A seminar is a student-led discussion of a text, and the questions that run it are
 * sorted by job: an *opening* question that anyone can answer from the text, *core*
 * questions that go back into specific passages, a *closing* question that asks what
 * changed, and *world-connection* questions that carry the ideas outside the text. A
 * good seminar question is open-ended — it has no single right answer and cannot be
 * settled by looking something up — and it sends the speaker back to the text.
 *
 * The questions here come from fixed frames filled with the teacher's or student's own
 * themes and passages, in a fixed order. The check for a question the student writes
 * is deliberately blunt: a yes/no opener or a missing question mark.
 */

export type SeminarCategory = "opening" | "core" | "closing" | "world";

export const SEMINAR_CATEGORIES: readonly SeminarCategory[] = [
  "opening",
  "core",
  "closing",
  "world",
];

/** How many generated questions each category offers at most. */
export const SEMINAR_LIMITS: Readonly<Record<SeminarCategory, number>> = {
  opening: 3,
  core: 8,
  closing: 3,
  world: 3,
};

export interface SeminarInput {
  title: string;
  author: string;
  /** The ideas to discuss, one per line. */
  themes: string;
  /** Passages worth returning to, one per line: "paragraph 3", "p. 12", "lines 9–14". */
  passages: string;
}

export const EMPTY_SEMINAR: SeminarInput = {
  title: "",
  author: "",
  themes: "",
  passages: "",
};

/** English frames. `{theme}`, `{theme2}`, `{passage}` and `{title}` are the student's words. */
export const SEMINAR_FRAMES = {
  opening: {
    theme: "In your own words, what does {title} suggest about {theme}?",
    idea: "What is one idea in {title} that you want to talk about, and why?",
    moment:
      "Which word, phrase, or moment in {title} stayed with you after you read it? What made it stand out?",
  },
  core: {
    develop: "How does {title} develop the idea of {theme}?",
    passage: "Reread {passage}. What does it suggest about {theme}?",
    both: "Is {theme} presented as a strength, a weakness, or both? What evidence supports your view?",
    tension:
      "Where do {theme} and {theme2} come into tension in {title}? What does that tension reveal?",
    word: "What is the most important word or phrase in {passage}, and why?",
    question: "What is {title} asking its readers to question about {theme}?",
    central:
      "What is the central question {title} raises, and where do you see it most clearly?",
    unresolved:
      "Where does {title} leave something unresolved or seem to contradict itself? Why might the author do that?",
  },
  closing: {
    changed:
      "Now that we have talked, has your view of {title} changed? What changed it?",
    newQuestion:
      "What new question about {theme} do you leave this discussion with?",
    classmate:
      "Which classmate's comment changed or deepened your thinking, and how?",
  },
  world: {
    life: "Where do you see {theme} in your own life, school, or community today?",
    today:
      "How might someone facing {theme} today respond differently from the people in {title}?",
    readers: "What should readers today take from {title}?",
    debate:
      "If {theme} were the topic of a public debate, what would the strongest view on each side be?",
  },
} as const;

export type SeminarFrames = typeof SEMINAR_FRAMES;

export interface SeminarQuestions {
  opening: string[];
  core: string[];
  closing: string[];
  world: string[];
}

const fill = (
  frame: string,
  values: Record<string, string | null>
): string | null => fillFrame(frame, values);

/**
 * The questions each category offers, in a fixed order, from the title, themes, and
 * passages. `fallbackTitle` stands in for an untitled text ("the text").
 */
export function buildSeminarQuestions(
  input: SeminarInput,
  frames: SeminarFrames,
  fallbackTitle: string
): SeminarQuestions {
  const title = tidy(input.title) === "" ? fallbackTitle : tidy(input.title);
  const themes = splitItems(input.themes);
  const passages = splitItems(input.passages);
  const base = { title };
  const themed = (frame: string, index: number): string | null => {
    const theme = themes[index];
    return theme === undefined ? null : fill(frame, { ...base, theme });
  };
  const keep = (list: (string | null)[], limit: number): string[] =>
    unique(list.filter((q): q is string => q !== null)).slice(0, limit);

  const opening = keep(
    [
      themed(frames.opening.theme, 0),
      fill(frames.opening.idea, base),
      fill(frames.opening.moment, base),
    ],
    SEMINAR_LIMITS.opening
  );

  const core: (string | null)[] = [];
  themes.forEach((theme, index) => {
    core.push(fill(frames.core.develop, { ...base, theme }));
    const passage = passages[index % Math.max(passages.length, 1)];
    if (passage !== undefined) {
      core.push(fill(frames.core.passage, { ...base, theme, passage }));
    }
    core.push(fill(frames.core.both, { ...base, theme }));
  });
  if (themes.length > 1) {
    themes.forEach((theme, index) => {
      const theme2 = themes[(index + 1) % themes.length];
      if (themes.length > 2 || index === 0) {
        core.push(fill(frames.core.tension, { ...base, theme, theme2 }));
      }
    });
  }
  for (const passage of passages) {
    core.push(fill(frames.core.word, { ...base, passage }));
  }
  for (const theme of themes) {
    core.push(fill(frames.core.question, { ...base, theme }));
  }
  core.push(
    fill(frames.core.central, base),
    fill(frames.core.unresolved, base)
  );

  const closing = keep(
    [
      fill(frames.closing.changed, base),
      themed(frames.closing.newQuestion, 0),
      fill(frames.closing.classmate, base),
    ],
    SEMINAR_LIMITS.closing
  );

  const world = keep(
    [
      themed(frames.world.life, 0),
      themed(frames.world.today, 0),
      fill(frames.world.readers, base),
      themed(frames.world.debate, themes.length > 1 ? 1 : 0),
    ],
    SEMINAR_LIMITS.world
  );

  return {
    opening,
    core: keep(core, SEMINAR_LIMITS.core),
    closing,
    world,
  };
}

function unique(list: string[]): string[] {
  return [...new Set(list)];
}

/* ------------------------------------------------------------------ question check */

const RE_CLOSED_OPENER =
  /^(?:is|are|was|were|do|does|did|can|will|has|have|had|shall)\b/i;

export type QuestionIssue = "closed" | "noMark";

/**
 * Two blunt checks on a question the student wrote. A yes/no opener ("Is …", "Did …")
 * usually has a one-word answer, and a missing question mark means it is a statement.
 * "Would", "could" and "should" are left alone: "Would you …?" can be closed, but
 * "Would the story change if …?" is a fine seminar question and the check cannot tell.
 */
export function questionIssues(question: string): QuestionIssue[] {
  const text = tidy(question);
  if (text === "") {
    return [];
  }
  const issues: QuestionIssue[] = [];
  if (RE_CLOSED_OPENER.test(text)) {
    issues.push("closed");
  }
  if (!text.endsWith("?")) {
    issues.push("noMark");
  }
  return issues;
}

/* ------------------------------------------------------------------ sheet */

export interface SeminarLabels {
  docTitle: string;
  subtitle: string;
  subtitleTitleOnly: string;
  subtitleBlank: string;
  fields: { name: string; date: string };
  headings: Readonly<Record<SeminarCategory, string>> & {
    before: string;
    evidence: string;
    norms: string;
    starters: string;
    reflection: string;
  };
  guidance: Readonly<Record<SeminarCategory, string>>;
  beforeChecklist: readonly string[];
  evidenceHead: readonly [string, string];
  norms: readonly string[];
  starters: readonly string[];
  reflectionPrompts: readonly string[];
  prompts: Readonly<Record<SeminarCategory, string>>;
}

export interface SeminarSheetOptions {
  /** Ruled lines under every question, for handwritten answers. */
  answerLines: boolean;
}

/** Ruled lines under a question; the opening and closing ones need fewer. */
const LINES: Readonly<Record<SeminarCategory, number>> = {
  opening: 3,
  core: 3,
  closing: 3,
  world: 3,
};

function questionBlocks(
  category: SeminarCategory,
  questions: readonly string[],
  options: SeminarSheetOptions,
  labels: SeminarLabels
): DocBlock[] {
  const blocks: DocBlock[] = [
    { kind: "guidance", text: labels.guidance[category] },
  ];
  if (questions.length === 0) {
    blocks.push({ kind: "prompt", text: labels.prompts[category] });
    return blocks;
  }
  questions.forEach((question, index) => {
    blocks.push({
      kind: "paragraph",
      text: [{ text: `${index + 1}. `, bold: true }, { text: question }],
    });
    if (options.answerLines) {
      blocks.push({ kind: "lines", count: LINES[category] });
    }
  });
  return blocks;
}

/** The printable preparation sheet for the selected questions. */
export function buildSeminarSheet(
  input: SeminarInput,
  selected: SeminarQuestions,
  options: SeminarSheetOptions,
  labels: SeminarLabels
): DocumentModel {
  const title = tidy(input.title);
  const author = tidy(input.author);
  let subtitle = labels.subtitleBlank;
  if (title !== "") {
    subtitle =
      author === ""
        ? t(labels.subtitleTitleOnly, { title })
        : t(labels.subtitle, { title, author });
  }
  const category = (key: SeminarCategory): DocSection => ({
    heading: labels.headings[key],
    blocks: questionBlocks(key, selected[key], options, labels),
  });
  const sections: DocSection[] = [
    {
      blocks: [
        {
          kind: "fields",
          fields: [
            { label: labels.fields.name },
            { label: labels.fields.date },
          ],
        },
      ],
    },
    {
      heading: labels.headings.before,
      blocks: [
        {
          kind: "checklist",
          items: labels.beforeChecklist.map((text) => ({ text })),
        },
      ],
    },
    category("opening"),
    category("core"),
    category("closing"),
    category("world"),
    {
      heading: labels.headings.evidence,
      blocks: [
        {
          kind: "table",
          head: labels.evidenceHead,
          rows: [
            ["", ""],
            ["", ""],
            ["", ""],
          ],
        },
      ],
    },
    {
      heading: labels.headings.norms,
      blocks: [
        {
          kind: "list",
          list: {
            style: "bullet",
            items: labels.norms.map((text) => ({ text })),
          },
        },
      ],
    },
    {
      heading: labels.headings.starters,
      blocks: [
        {
          kind: "list",
          list: {
            style: "bullet",
            items: labels.starters.map((text) => ({ text })),
          },
        },
      ],
    },
    {
      heading: labels.headings.reflection,
      blocks: labels.reflectionPrompts.flatMap((text): DocBlock[] => [
        { kind: "paragraph", text },
        { kind: "lines", count: 2 },
      ]),
    },
  ];
  return { title: labels.docTitle, subtitle, sections };
}

/** The generated questions the student kept, then the ones they wrote themselves. */
export function selectQuestions(
  generated: SeminarQuestions,
  excluded: ReadonlySet<string>,
  own: Readonly<Record<SeminarCategory, string>>
): SeminarQuestions {
  const pick = (category: SeminarCategory): string[] => [
    ...generated[category].filter((question) => !excluded.has(question)),
    ...splitItems(own[category]),
  ];
  return {
    opening: pick("opening"),
    core: pick("core"),
    closing: pick("closing"),
    world: pick("world"),
  };
}
